import { requireData, requireSuccess } from "../../lib/appError";
import type { Database, Json } from "../../lib/database.types";
import { supabase } from "../../lib/supabaseClient";
import type { SiteBlockInsert, SiteBlockRow, SiteBlockUpdate } from "../../lib/blockContentSchemas";

type Block = SiteBlockRow;
type BlockInsert = SiteBlockInsert;
type BlockUpdate = SiteBlockUpdate;

export const blockRepository = {
  async saveDocument(pageId:string,previous:Block[],next:Block[]):Promise<Block[]> {
    const {data,error}=await supabase.rpc('save_page_document',{p_page_id:pageId,p_expected:previous.map(({id,updated_at})=>({id,updated_at})),p_blocks:next as unknown as Json});
    return requireData(data,error,'Could not save your page. Your edits are still here.');
  },
  async saveDocuments(documents:{page_id:string;previous:Block[];blocks:Block[]}[]) {
    const {data,error}=await supabase.rpc('save_page_documents',{p_documents:documents.map(d=>({page_id:d.page_id,expected:d.previous.map(({id,updated_at})=>({id,updated_at})),blocks:d.blocks})) as unknown as Json});
    return requireData(data,error,'Could not move blocks. Your edits are still here.') as unknown as {page_id:string;blocks:Block[]}[];
  },

  async list(pageId: string): Promise<Block[]> {
    const { data, error } = await supabase
      .from("blocks")
      .select("*")
      .eq("page_id", pageId)
      .order("position");
    return requireData(data, error, "Could not load blocks.");
  },

  async listForVersion(siteVersionId: string): Promise<Block[]> {
    const { data, error } = await supabase
      .from("blocks")
      .select("*")
      .eq("site_version_id", siteVersionId)
      .order("position");
    return requireData(data, error, "Could not load your pages.");
  },

  async firstPicture(pageId: string): Promise<Block | null> {
    const { data, error } = await supabase.from("blocks").select("*").eq("page_id", pageId).eq("block_type", "picture").order("position").limit(1).maybeSingle();
    requireSuccess(error, "Could not load page thumbnail.");
    return data;
  },

  async create(values: BlockInsert): Promise<Block> {
    const { data, error } = await supabase
      .from("blocks")
      // Widened block types until database.types.ts is regenerated.
      .insert(values as Database["public"]["Tables"]["blocks"]["Insert"])
      .select()
      .single();
    return requireData(data, error, "Could not create the block.");
  },

  async update(blockId: string, values: BlockUpdate): Promise<Block> {
    const { data, error } = await supabase
      .from("blocks")
      .update(values as Database["public"]["Tables"]["blocks"]["Update"])
      .eq("id", blockId)
      .select()
      .single();
    return requireData(data, error, "Could not update the block.");
  },

  async remove(blockId: string) {
    const { error } = await supabase.from("blocks").delete().eq("id", blockId);
    requireSuccess(error, "Could not delete the block.");
  },

  async reorder(
    pageId: string,
    parentSourceBlockId: string | null,
    orderedSourceBlockIds: string[],
  ) {
    const { error } = await supabase.rpc("reorder_blocks", {
      p_page_id: pageId,
      // Generated types cannot express nullable function args; null means "root level".
      p_parent_source_block_id: parentSourceBlockId as string,
      p_ordered_source_block_ids: orderedSourceBlockIds,
    });
    requireSuccess(error, "Could not reorder the blocks.");
  },
};
